import type { AssetListItem, GetAssetsParams } from '@cartwhl/common/api/getAssets';
import type {
  OrganizationMember,
  UserInfo,
} from '@cartwhl/common/types';
import {
  endOfYear,
  startOfDay,
  startOfYear,
  subDays,
  subYears,
} from 'date-fns';

import {
  isComicBatchGroupID,
  isProjectGroupID,
  isSwingBatchGroupID,
} from './assetLibraryGroupIds';
import type { AssetLibraryEmbedConfig } from './assetLibraryEmbedConfig';
import type { AssetLibraryView } from './assetLibraryView';

export type AssetLibraryTypeFilter =
  | 'all'
  | 'scene'
  | 'canvas-project'
  | 'comic-batch'
  | 'mascot-job'
  | 'swing-batch'
  | 'image'
  | 'video';

export type AssetLibraryLastModifiedFilter =
  | 'all'
  | 'today'
  | 'last-7-days'
  | 'last-30-days'
  | 'this-year'
  | 'last-year';

export type AssetLibraryFilters = {
  typeFilter: AssetLibraryTypeFilter;
  lastModifiedFilter: AssetLibraryLastModifiedFilter;
  createdByUser: string;
  searchQuery: string;
};

export const INITIAL_ASSET_LIBRARY_FILTERS: AssetLibraryFilters = {
  typeFilter: 'all',
  lastModifiedFilter: 'all',
  createdByUser: '',
  searchQuery: '',
};

export function hasActiveAssetLibraryFilters(
  filters: AssetLibraryFilters,
): boolean {
  return (
    filters.typeFilter !== 'all' ||
    filters.lastModifiedFilter !== 'all' ||
    Boolean(filters.createdByUser) ||
    Boolean(filters.searchQuery.trim())
  );
}

export function shouldAutoLoadMoreLibraryAssets(
  filters: AssetLibraryFilters,
  libraryView: AssetLibraryView,
): boolean {
  return hasActiveAssetLibraryFilters(filters) || libraryView !== 'all';
}

export const ASSET_LIBRARY_TYPE_FILTER_OPTIONS: Array<{
  value: Exclude<AssetLibraryTypeFilter, 'all'>;
  label: string;
}> = [
  { value: 'scene', label: 'Cartwheel Scenes' },
  { value: 'canvas-project', label: 'Canvas Projects' },
  { value: 'comic-batch', label: 'Comic Jobs' },
  { value: 'mascot-job', label: 'Mascot Jobs' },
  { value: 'swing-batch', label: 'Swing Jobs' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Videos' },
];

const ASSET_LIBRARY_TYPE_FILTER_VALUES = new Set(
  ASSET_LIBRARY_TYPE_FILTER_OPTIONS.map((option) => option.value),
);

export function parseAssetLibraryTypeFilterFromSearchParam(
  value: string | null | undefined,
): AssetLibraryTypeFilter | null {
  if (!value) {
    return null;
  }

  if (value === 'all') {
    return 'all';
  }

  if (
    ASSET_LIBRARY_TYPE_FILTER_VALUES.has(
      value as Exclude<AssetLibraryTypeFilter, 'all'>,
    )
  ) {
    return value as AssetLibraryTypeFilter;
  }

  return null;
}

export function getAssetLibraryPathWithTypeFilter(
  typeFilter: Exclude<AssetLibraryTypeFilter, 'all'>,
): string {
  return `/library?type=${typeFilter}`;
}

export const ASSET_LIBRARY_LAST_MODIFIED_FILTER_OPTIONS: Array<{
  value: Exclude<AssetLibraryLastModifiedFilter, 'all'>;
  label: string;
}> = [
  { value: 'today', label: 'Today' },
  { value: 'last-7-days', label: 'Last 7 days' },
  { value: 'last-30-days', label: 'Last 30 days' },
  { value: 'this-year', label: 'This year' },
  { value: 'last-year', label: 'Last year' },
];

export function getAssetLibraryTypeFilterOptions(
  embedConfig?: AssetLibraryEmbedConfig | null,
): typeof ASSET_LIBRARY_TYPE_FILTER_OPTIONS {
  if (!embedConfig) {
    return ASSET_LIBRARY_TYPE_FILTER_OPTIONS;
  }

  const allowed = new Set(embedConfig.allowedTypeFilters);
  return ASSET_LIBRARY_TYPE_FILTER_OPTIONS.filter((option) =>
    allowed.has(option.value),
  );
}

export function buildGetAssetsParamsFromFilters(
  filters: AssetLibraryFilters,
  embedConfig?: AssetLibraryEmbedConfig | null,
): GetAssetsParams {
  const params: GetAssetsParams = {};

  if (filters.typeFilter === 'image') {
    params.assetType = 'MEDIA_IMAGE';
  } else if (filters.typeFilter === 'video') {
    params.assetType = 'MEDIA_VIDEO';
  } else if (filters.typeFilter === 'scene') {
    params.assetType = 'SCENE';
  } else if (filters.typeFilter === 'mascot-job') {
    params.assetType = 'MASCOT_JOB';
  } else if (filters.typeFilter !== 'all') {
    params.assetType = 'MOGEN_JOB';
  } else if (embedConfig?.allowedAssetTypes.length) {
    params.assetType =
      embedConfig.allowedAssetTypes.length === 1
        ? embedConfig.allowedAssetTypes[0]
        : [...embedConfig.allowedAssetTypes];
  }

  const lastUpdatedAt = buildLastUpdatedAtFilterParam(
    filters.lastModifiedFilter,
  );
  if (lastUpdatedAt) {
    params.lastUpdatedAt = lastUpdatedAt;
  }

  if (filters.createdByUser) {
    params.createdByUser = filters.createdByUser;
  }

  return params;
}

export function buildLastUpdatedAtFilterParam(
  filter: AssetLibraryLastModifiedFilter,
): string | undefined {
  if (filter === 'all') {
    return undefined;
  }

  const now = new Date();
  let start: Date;
  let end = now;

  switch (filter) {
    case 'today':
      start = startOfDay(now);
      break;
    case 'last-7-days':
      start = subDays(now, 7);
      break;
    case 'last-30-days':
      start = subDays(now, 30);
      break;
    case 'this-year':
      start = startOfYear(now);
      break;
    case 'last-year': {
      const lastYear = subYears(now, 1);
      start = startOfYear(lastYear);
      end = endOfYear(lastYear);
      break;
    }
    default:
      return undefined;
  }

  return `${start.toISOString()};${end.toISOString()}`;
}

export function assetMatchesTypeFilter(
  asset: AssetListItem,
  typeFilter: AssetLibraryTypeFilter,
): boolean {
  if (typeFilter === 'all') {
    return true;
  }

  if (typeFilter === 'image') {
    return asset.assetType === 'MEDIA_IMAGE';
  }

  if (typeFilter === 'video') {
    return asset.assetType === 'MEDIA_VIDEO';
  }

  if (typeFilter === 'scene') {
    return asset.assetType === 'SCENE';
  }

  if (typeFilter === 'mascot-job') {
    return asset.assetType === 'MASCOT_JOB';
  }

  if (asset.assetType !== 'MOGEN_JOB') {
    return false;
  }

  switch (typeFilter) {
    case 'canvas-project':
      return asset.groupID ? isProjectGroupID(asset.groupID) : false;
    case 'comic-batch':
      return asset.groupID ? isComicBatchGroupID(asset.groupID) : false;
    case 'swing-batch':
      return asset.groupID ? isSwingBatchGroupID(asset.groupID) : false;
    default:
      return true;
  }
}

export function isActiveOrganizationSeatMember(
  member: OrganizationMember,
): boolean {
  return !member.isOrgAdminOnly;
}

export const ANONYMOUS_ASSET_CREATOR_USER_ID = 'user-anonymous';
export const ANONYMOUS_ASSET_CREATOR_LABEL = 'API';

export function buildAssetPeopleOptions(
  organizationMembers: readonly OrganizationMember[] | undefined,
): Array<{ value: string; label: string }> {
  const memberOptions = (organizationMembers ?? [])
    .filter(isActiveOrganizationSeatMember)
    .map((member) => ({
      value: member.userID,
      label: formatUserDisplayName(member) || member.userID,
    }));

  return [
    ...memberOptions,
    {
      value: ANONYMOUS_ASSET_CREATOR_USER_ID,
      label: ANONYMOUS_ASSET_CREATOR_LABEL,
    },
  ].sort((left, right) => left.label.localeCompare(right.label));
}

export function formatUserDisplayName(user: {
  preferredName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}): string {
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

  return user.preferredName || fullName || user.email || '';
}

export function createAvatarFallbackUserInfo(userID: string): UserInfo {
  return {
    preferredName: '',
    firstName: userID,
    lastName: '',
    email: userID,
  } as UserInfo;
}

export function isAnonymousAssetCreator(
  userID: string | null | undefined,
): userID is typeof ANONYMOUS_ASSET_CREATOR_USER_ID {
  return userID === ANONYMOUS_ASSET_CREATOR_USER_ID;
}

export function isKnownAssetCreator(
  createdByUser: string | undefined,
): createdByUser is string {
  return (
    Boolean(createdByUser) &&
    !isAnonymousAssetCreator(createdByUser)
  );
}

export function isDisplayableAssetCreator(
  createdByUser: string | undefined,
): createdByUser is string {
  return (
    isKnownAssetCreator(createdByUser) ||
    isAnonymousAssetCreator(createdByUser)
  );
}

export function resolveAssetCreatorDisplayName(
  userID: string | null | undefined,
  organizationMembers: readonly OrganizationMember[] | undefined,
): string | null {
  if (!userID) {
    return null;
  }

  if (isAnonymousAssetCreator(userID)) {
    return ANONYMOUS_ASSET_CREATOR_LABEL;
  }

  const member = organizationMembers?.find(
    (organizationMember) => organizationMember.userID === userID,
  );

  if (member) {
    return formatUserDisplayName(member) || userID;
  }

  return null;
}

export function resolvePeopleUserInfo(
  userID: string,
  organizationMembers: readonly OrganizationMember[] | undefined,
): UserInfo {
  const member = organizationMembers?.find(
    (organizationMember) => organizationMember.userID === userID,
  );
  if (member) {
    return member as unknown as UserInfo;
  }

  return createAvatarFallbackUserInfo(userID);
}
